import React from 'react'
import { 
  ButtonGroup, Modal, Checkbox, Glyphicon,
  Form, FormGroup, ControlLabel, FormControl, Radio 
} from 'react-bootstrap'
import moment from 'moment'
import { connect } from 'react-redux'

import Button from '../components/Button.jsx'
import { round, printMoney } from '../../lib/Tools.js'
import { getText, getTextWithTokens } from '../lib/text.js'
import * as Actions from '../ducks/trips'
import { getRateEurYuan, getRateYuanEur, eur2yuan, yuan2eur } from '../lib/exchangeRateClient'
import HoverTip from '../components/HoverTip'

function roundingLabel(x) {
  if (!x)             return getText("Sin redondeo")
  else if (x === 1)   return getText("Unidades")
  else if (x === 10)  return getText("Decenas")
  else if (x === 100) return getText("Centenas") 
  else                return "ERROR" 
}

type Props = {
  trip: Object, 
  price: Object, 
  close: () => void,
  
  updateTrip: Object => void, 
  convert: string => void,
}

const mapStateToProps = state => ({})

const mapDispatchToProps = (dispatch, props) => ({
  
  updateTrip: changes => dispatch(Actions.updateTrip(props.trip._id, changes)),
  
  convert: (to: "e"|"y") => dispatch(Actions.updateTrip(props.trip._id, {
    conversionTime: moment().format("YYYY-MM-DD HH:mm:ss"), 
    conversionRate: getRateEurYuan(), 
    [to == "e" ? "forcedPriceEur" : "forcedPriceYuan"]: round(
      to == "e" ? yuan2eur(props.trip.forcedPriceYuan) : eur2yuan(props.trip.forcedPriceEur),
      0.01 
    ), 
  }))
})

const PriceSettingsModal = (props: Props) => {
  const { trip, price, updateTrip, close, convert } = props

  return (
    <Modal show bsSize="small" onHide={() => close()}>
      <Modal.Header closeButton>{getText("Configurar precio")}</Modal.Header>
      <Modal.Body>

        {/* precio a mano o calculado */}
        
        <FormGroup>
          <Radio checked={trip.useForcedPrice} onClick={() => updateTrip({ useForcedPrice: true })}>{getText("Precio a mano")}</Radio>
          <Radio checked={!trip.useForcedPrice} onClick={() => updateTrip({ useForcedPrice: false })}>{getText("Precio automático")}</Radio>
        </FormGroup>

        {
          trip.useForcedPrice 
            ? (
              <div>
                <Form inline>
                  <FormGroup>
                    <ControlLabel>{getText("Precio en Yuanes")}: </ControlLabel>
                    <br />
                    <FormControl 
                      componentClass="input" 
                      type="number"
                      style={{ width: 130 }}
                      value={trip.forcedPriceYuan || 0}
                      onChange={evt => updateTrip({ 
                        forcedPriceYuan: parseInt(evt.target.value), 
                        conversionTime: null, 
                        conversionRate: null 
                      })}
                    />
                    <span>元 </span>
                    <span>(hoy {printMoney(yuan2eur(trip.forcedPriceYuan))}€)</span>
                  </FormGroup>

                  <ButtonGroup
                    style={{ marginTop: 15, marginBottom: 15 }}
                  >
                    <Button 
                      tooltipText="Usar este precio en Yuanes, convertido a Euros" 
                      glyph="arrow-down" 
                      onClick={() => convert("e")}
                    >
                      Yuan a Euro
                    </Button>
                    <Button 
                      tooltipText="Usar este precio en Euros, convertido a Yuanes" 
                      glyph="arrow-up" 
                      onClick={() => convert("y")}
                    >
                      Euro a Yuan
                    </Button>
                  </ButtonGroup>
                  
                  <FormGroup>
                    <ControlLabel>{getText("Precio en Euros")}: </ControlLabel>
                    <br />
                    <FormControl 
                      componentClass="input" 
                      type="number"
                      style={{ width: 130 }}
                      value={trip.forcedPriceEur || 0}
                      onChange={evt => updateTrip({ 
                        forcedPriceEur: parseInt(evt.target.value), 
                        conversionTime: null, 
                        conversionRate: null 
                      })}
                    />
                    <span>€ </span>
                    <span>(hoy ¥{printMoney(eur2yuan(trip.forcedPriceEur))}元)</span>
                  </FormGroup>
                </Form>
                <br />
                {
                  trip.conversionRate && (
                    <HoverTip 
                      text={
                        `Se fijó el precio a mano, y se convirtió a ${trip.conversionRate} yuanes por euro, ` 
                        + `que era el cambio a ${trip.conversionTime}`
                      }
                    >
                      <p>{`Tipo de cambio: ${trip.conversionRate} ¥/€`} <Glyphicon glyph="question-sign" /></p>
                    </HoverTip>
                  )
                }
                <HoverTip text="Precio que la plataforma calcula para este viaje, incluyendo los gastos de gestión">
                  <p>
                    {getTextWithTokens("price_with_pctg", [trip.pGestion])}: <strong>{printMoney(price.calculatedFinalE)}€ </strong>
                    <Glyphicon glyph="question-sign" />
                  </p>
                </HoverTip>
                <HoverTip text="Precio que la plataforma calcula para este viaje, sin incluir los gastos de gestión">
                  <p>
                    {getText("Precio calculado sin %")}: <strong>{printMoney(price.calculatedBruteE)}€ </strong>
                    <Glyphicon glyph="question-sign" />
                  </p>
                </HoverTip>
              </div>
            )
            : (
              <div>
                <Form inline>
                  <FormGroup controlId="gastos_gestion">
                    <ControlLabel>{getText("% Gastos de Gestión")}</ControlLabel><br />
                    <FormControl
                      type="number"
                      value={trip.pGestion || 0}
                      onChange={evt => {
                        let v = Number.parseInt(evt.target.value)
                        if (Number.isNaN(v)) { v = 0 }
                        updateTrip({ pGestion: v })
                      }}
                    />
                  </FormGroup>
                  <br />
                  <FormGroup style={{ marginTop: 20 }}>
                    <ControlLabel>{getText("Redondear a")}: </ControlLabel><br />
                    <FormControl
                      componentClass="select"
                      style={{ marginLeft: 10 }}
                      value={trip.rounding}
                      onChange={evt => updateTrip({ rounding: parseInt(evt.target.value) })}
                    >
                      {
                        [0, 1, 10, 100].map(x => (
                          <option key={`${x}`} value={x}>{roundingLabel(x)}</option>
                        ))
                      }
                    </FormControl>
                  </FormGroup>
                  <br />
                  <Checkbox 
                    checked={trip.onlyRoundUp}
                    style={{ marginTop: 10 }} 
                    onChange={evt => {
                      updateTrip({ onlyRoundUp: evt.target.checked })
                    }}
                  >
                    {"  " + getText("Sólo redondear hacia arriba")}
                  </Checkbox>
                </Form>
                <br />
                <HoverTip text={`Precio total en yuanes, al cambio de hoy (${round(getRateYuanEur(), 0.0001)}) yuanes por euro`}>
                  <p>
                    {getText("Precio en yuanes")}: <strong>￥{printMoney(price.finalY)}元 </strong>
                    <Glyphicon glyph="question-sign" />
                  </p>
                </HoverTip>
                <HoverTip text="Precio que la plataforma calcula para este viaje, incluyendo el porcentaje de gestión">
                  <p>
                    {getText("Precio en euros")}: <strong>{printMoney(price.finalE)}€ </strong>
                    <Glyphicon glyph="question-sign" />
                  </p>
                </HoverTip>
                <HoverTip text="Precio que la plataforma calcula para este viaje, sin incluir los gastos de gestión">
                  <p>
                    {getText("Sin Gº de gestión")}: <strong>{printMoney(price.calculatedBruteE)}€ </strong>
                    <Glyphicon glyph="question-sign" />
                  </p>
                </HoverTip>
              </div>
            )
        }

      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => close()}>{getText("OK")}</Button>
      </Modal.Footer>
    </Modal>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(PriceSettingsModal)
